/** @jsxImportSource @emotion/react */

import { Form as FinalForm } from 'react-final-form'
import { FC, useMemo } from 'react'
import Button from '@mui/material/Button'
import { addDays, differenceInCalendarDays, isAfter } from 'date-fns'
import { FormApi } from 'final-form'
import { Dictionary } from 'lodash'
import {
  MembershipInput,
  MembershipStatus,
  MembershipType,
  PlatformName,
  ProductTypeEnum,
  SubscriptionStatus,
} from '__generatedTypes__/globalTypes'
import { findOneUser_findOneUser_membership as Membership } from '__generatedTypes__/findOneUser'
import { SelectField } from 'components/Select'
import { findAllPlatforms_findAllPlatforms_items as Platform } from '__generatedTypes__/findAllPlatforms'
import { DateTimeInputField } from 'components/DateTimeInput'
import { getFullPlatformTitle } from 'components/Layout/Sidebar/PlatformList'
import { validateIsNotInFuture, validateRequired } from 'utils/validation'
import { InputField } from 'components/Input'
import { pluralize } from 'utils/pluralize'
import { toast } from 'utils/toast'
import { findAllProduct_findAllProduct_items as Product } from '__generatedTypes__/findAllProduct'
import { getMembershipTitle } from 'utils/getMembershipTitle'
import { asUTC } from 'utils/asUtc'
import { stripTimezone } from 'utils/stripTimezone'
import { PlatformSlug } from 'constants/consts'
import { isMembershipsAvailable } from 'pagesNew/Products/platformConfig'
import * as classes from './styles'

export type SelectedPlatform = PlatformName | null

export type MembershipFormItem = {
  isNew: boolean
} & Omit<Partial<Membership>, '__typename'>

type Props = {
  index: number
  userId: string
  isSubmitDisabled: boolean
  membership: MembershipFormItem
  membershipList: Membership[]
  platforms: Platform[]
  products: Dictionary<Product[]>
  selectedPlatforms: SelectedPlatform[]
  onSelectPlatform: (index: number, platform: PlatformName | null) => void
  onSubmitMembershipForm: (values: MembershipInput, index: number) => Promise<void>
  isReadOnly?: boolean
}

type MembershipStatusValue = {
  id: MembershipStatus
  title: MembershipStatus
}

type FormValues = {
  platform: {
    id: PlatformName
    title: PlatformName
  }
  isNotEditableSubscriptionMembership: boolean
  membershipStatus: MembershipStatusValue
  pausedAt: Date
  startDate: Date | null
  expirationDate: Date | null
  product: {
    id: string
    title: string
  } | null
  daysRemaining: string
}

const MEMBERSHIP_STATUS_ITEMS = [
  { id: MembershipStatus.Active, title: MembershipStatus.Active },
  { id: MembershipStatus.Expired, title: MembershipStatus.Expired },
  { id: MembershipStatus.Paused, title: MembershipStatus.Paused },
]

const MAP_MEMBERSHIP_TYPE_TO_PRODUCT_TYPE: Record<MembershipType, ProductTypeEnum> = {
  [MembershipType.Free]: ProductTypeEnum.Free,
  [MembershipType.Premium]: ProductTypeEnum.Premium,
  [MembershipType.PremiumMax]: ProductTypeEnum.PremiumMax,
} as const

const getMembershipTypeFromProductType = (productType: ProductTypeEnum): MembershipType =>
  Object.keys(MAP_MEMBERSHIP_TYPE_TO_PRODUCT_TYPE).find(
    (key) => MAP_MEMBERSHIP_TYPE_TO_PRODUCT_TYPE[key as MembershipType] === productType
  ) as MembershipType

const validateStartDate = (value: Date | null) =>
  validateIsNotInFuture('Start Date should not be in future')(value) || validateRequired(value)

export const MembershipForm: FC<Props> = ({
  index,
  userId,
  membership,
  membershipList,
  platforms: platformsProp,
  products: productsProp,
  selectedPlatforms,
  isSubmitDisabled,
  onSelectPlatform,
  onSubmitMembershipForm,
  isReadOnly,
}) => {
  const { isNew, pauseTokensCount = 0, additionalTestsSubjectIds = [] } = membership

  const getMembershipItems = (platform: PlatformName) => {
    const platformProducts = productsProp[platform || ''] || []

    return platformProducts.map((product) => ({
      id: product.id,
      title: getMembershipTitle(product),
    }))
  }

  const getInitialProduct = (platformTitle?: PlatformName, type?: MembershipType, duration?: string | null) => {
    if (!platformTitle || !type || !duration) {
      return null
    }

    const productType = MAP_MEMBERSHIP_TYPE_TO_PRODUCT_TYPE[type]

    return productsProp[platformTitle]?.find((product) => product.type === productType && product.duration === duration)
  }

  const getProductById = (productId: string) => {
    const platformProducts = Object.values(productsProp).flat()

    return platformProducts.find(({ id }) => id === productId)
  }

  const initialValues: FormValues | undefined = useMemo(() => {
    const { platform, type, startDate, expiresTo, status, duration, pausedAt } = membership

    if (isNew || !platform) {
      return
    }

    const strippedExpiresTo = expiresTo ? stripTimezone(String(expiresTo)) : new Date()

    const daysLeft = differenceInCalendarDays(new Date(strippedExpiresTo), pausedAt ? new Date(pausedAt) : new Date())
    const daysRemaining = daysLeft >= 0 ? daysLeft : 0

    const membershipProduct = getInitialProduct(platform?.title, type, duration)

    return {
      platform: {
        id: platform?.title as PlatformName,
        title: platform?.title as PlatformName,
      },
      // Admin can't edit membership if it's a subscription and (status is active or subscription is not canceled)
      isNotEditableSubscriptionMembership:
        !!membership.subscriptionId &&
        (membership.status === MembershipStatus.Active ||
          membership.subscriptionStatus !== SubscriptionStatus.Canceled),
      product: membershipProduct
        ? {
            id: membershipProduct.id,
            title: getMembershipTitle(membershipProduct),
          }
        : null,
      startDate: new Date(startDate),
      pausedAt: pausedAt ? new Date(pausedAt) : new Date(),
      membershipStatus: {
        id: status as MembershipStatus,
        title: status as MembershipStatus,
      },
      expirationDate: expiresTo ? new Date(strippedExpiresTo) : new Date(),
      daysRemaining: `${daysRemaining}-${pluralize(daysRemaining, 'day', 'days')}`,
    }
  }, [membership, isNew])

  const platforms = useMemo(
    () =>
      platformsProp
        .filter(({ title }) => !selectedPlatforms.includes(title))
        .filter(({ slug }) => isMembershipsAvailable(slug as PlatformSlug))
        .map(({ title }) => ({
          id: title,
          title,
        })),
    [platformsProp, selectedPlatforms]
  )

  const handleSubmit = async (values: FormValues) => {
    try {
      const { product, membershipStatus, startDate, expirationDate, pausedAt = new Date() } = values

      const membershipId = isNew
        ? membershipList.find(({ platform }) => platform.title === values.platform.title)?.id
        : membership.id

      if (!membershipId) {
        throw new Error('Membership ID is not found')
      }

      const membershipProduct = getProductById(product?.id || '')
      const initialMembershipId = initialValues?.product?.id
      const isMembershipChanged = initialMembershipId && initialMembershipId !== product?.id

      const membershipType = getMembershipTypeFromProductType(membershipProduct?.type || ProductTypeEnum.Free)

      const expiresTo = asUTC(expirationDate || new Date())

      await onSubmitMembershipForm(
        {
          id: membershipId,
          type: membershipType,
          expiresTo,
          user: {
            id: userId,
          },
          isExtended: isMembershipChanged ? false : !!membership.isExtended,
          extensionType: isMembershipChanged ? null : membership.extensionType,
          status: membershipStatus.id,
          startDate,
          pausedAt: membershipStatus.id === MembershipStatus.Paused ? pausedAt?.toISOString() : null,
          pauseTokensCount,
          additionalTestsSubjectIds,
          duration: membershipProduct?.duration || null,
          pauseTokensUsed: membership.pauseTokensUsed || 0,
          order: index + 1,
          // If this was a subscription membership being edited (only possible when Expired + Canceled),
          // clear subscription data since it's no longer managed by Stripe
          // For regular memberships, subscription data is always empty, so nothing will be changed
          subscriptionId: null,
          subscriptionStatus: null,
        },
        index
      )
    } catch (error) {
      toast.error('Failed to prepare membership data for submission')
    }
  }

  const handleMembershipPlatformChange = (form: FormApi<FormValues>) => () => {
    const { platform, product } = form.getState().values

    const getNewProduct = (platformTitle?: PlatformName, type?: ProductTypeEnum) => {
      if (!platformTitle || !type) {
        return null
      }

      return productsProp[platformTitle]?.find((product) => product.type === type)
    }

    const currenProduct = getProductById(product?.id || '')
    const newProduct = getNewProduct(platform?.title, currenProduct?.type)

    if (newProduct) {
      form.change('product', {
        id: newProduct.id,
        title: getMembershipTitle(newProduct),
      })
    } else {
      form.change('product', null)
    }

    handleMembershipProductChange(form)()

    onSelectPlatform(index, platform?.id || null)
  }

  const handleMembershipProductChange = (form: FormApi<FormValues>) => () => {
    const { startDate, product, membershipStatus } = form.getState().values
    const currentDate = new Date()

    if (!product || membershipStatus?.id === MembershipStatus.Expired) {
      return
    }

    const membershipProduct = getProductById(product.id)

    if (!membershipProduct) {
      return
    }

    const days = Number(membershipProduct.duration)
    let membershipStartDate = startDate

    if (!membershipStartDate) {
      form.change('startDate', currentDate)
      membershipStartDate = currentDate
    }

    const newExpirationDate = addDays(membershipStartDate, days)
    const daysDiff = differenceInCalendarDays(newExpirationDate, currentDate)
    const daysLeft = Math.max(daysDiff, 0)
    const newMembershipStatus = daysDiff >= 0 ? MembershipStatus.Active : MembershipStatus.Expired

    form.change('expirationDate', newExpirationDate)
    form.change('daysRemaining', `${daysLeft}-${pluralize(daysLeft, 'day', 'days')}`)
    form.change('membershipStatus', {
      id: newMembershipStatus,
      title: newMembershipStatus,
    })
  }

  const handleMembershipStatusChange = (form: FormApi<FormValues>) => (value: MembershipStatusValue | null) => {
    const currentDate = new Date()

    if (value?.id === MembershipStatus.Expired) {
      const newExpirationDate = addDays(currentDate, -1)

      form.change('expirationDate', newExpirationDate)
      form.change('daysRemaining', '0-days')
    }
  }

  const handleExpirationDateChange = (form: FormApi<FormValues>) => (value: Date | null) => {
    const currentDate = new Date()

    if (value) {
      const daysDiff = differenceInCalendarDays(value, currentDate)
      const daysLeft = Math.max(daysDiff, 0)
      const newMembershipStatus = daysDiff >= 0 ? MembershipStatus.Active : MembershipStatus.Expired

      form.change('daysRemaining', `${daysLeft}-${pluralize(daysLeft, 'day', 'days')}`)
      form.change('membershipStatus', {
        id: newMembershipStatus,
        title: newMembershipStatus,
      })
    }
  }

  const validate = (values: FormValues) => {
    const { membershipStatus, startDate, expirationDate } = values

    const errors: Record<string, string> = {}

    if (startDate && expirationDate && !isAfter(expirationDate, startDate)) {
      errors.startDate = 'Start Date should be before Expiration Date'
      errors.expirationDate = 'Expiration Date should be after Start Date'
    }

    if (
      expirationDate &&
      differenceInCalendarDays(expirationDate, new Date()) >= 0 &&
      membershipStatus?.id === MembershipStatus.Expired
    ) {
      errors.membershipStatus = 'Cannot expire membership with expiration date in future'
    }

    if (
      expirationDate &&
      differenceInCalendarDays(expirationDate, new Date()) < 0 &&
      membershipStatus?.id === MembershipStatus.Active
    ) {
      errors.membershipStatus = 'This membership cannot be active because the expiration date is in the past'
    }

    return errors
  }

  return (
    <FinalForm
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validate={validate}
      render={({ form, submitting, values, handleSubmit }) => {
        const isMembershipExpired =
          values.expirationDate && differenceInCalendarDays(values.expirationDate, new Date()) < 0
        const isMembershipPaused = values.membershipStatus?.id === MembershipStatus.Paused
        const isFieldsDisabled = isReadOnly || values.isNotEditableSubscriptionMembership

        return (
          <form css={classes.form} onSubmit={handleSubmit} data-testid={`membership-form-${index}`}>
            <SelectField
              name="platform"
              label="Platform"
              validate={validateRequired}
              items={
                values.platform?.id
                  ? [
                      {
                        id: values.platform?.id,
                        title: values.platform?.title,
                      },
                      ...platforms,
                    ]
                  : platforms
              }
              renderItem={(platform) => getFullPlatformTitle(platform.title)}
              getItemLabel={(platform) => getFullPlatformTitle(platform.title) ?? platform.title}
              placeholder="Platform"
              onChange={handleMembershipPlatformChange(form)}
              fullWidth
              disabled={isReadOnly || !isNew}
              data-testid="platform-select"
            />
            <SelectField
              name="product"
              label="Membership"
              onChange={handleMembershipProductChange(form)}
              validate={validateRequired}
              items={getMembershipItems(values.platform?.title)}
              placeholder="Membership"
              fullWidth
              data-testid="membership-select"
              disabled={isFieldsDisabled}
            />

            <div css={classes.column}>
              <SelectField
                name="membershipStatus"
                label="Membership Status"
                items={MEMBERSHIP_STATUS_ITEMS}
                placeholder="Membership Status"
                onChange={handleMembershipStatusChange(form)}
                validate={validateRequired}
                fullWidth
                displayErrorIfUntouched
                disabledItemIds={isMembershipExpired ? [MembershipStatus.Active, MembershipStatus.Paused] : []}
                data-testid="membership-status-select"
                disabled={isFieldsDisabled}
              />
              <DateTimeInputField
                name="startDate"
                validate={validateStartDate}
                label="Start Date"
                placeholder="Enter Date"
                fullWidth
                pickerType="date"
                format="MMMM d, yyyy"
                withConfirmation={false}
                data-testid="start-date-input"
                disabled={isFieldsDisabled}
              />
            </div>
            <div css={classes.column}>
              {!isMembershipPaused ? (
                <DateTimeInputField
                  name="expirationDate"
                  validate={validateRequired}
                  onChange={handleExpirationDateChange(form)}
                  label="Expiration Date"
                  placeholder="Enter Date"
                  fullWidth
                  pickerType="date"
                  format="MMMM d, yyyy"
                  withConfirmation={false}
                  data-testid="expiration-date-input"
                  disabled={isFieldsDisabled}
                />
              ) : null}

              <InputField
                name="daysRemaining"
                disabled
                fullWidth
                placeholder="Days remaining"
                label="Days remaining"
                data-testid="days-remaining-input"
              />
            </div>

            <Button
              css={classes.submitButton}
              variant="outlined"
              type="submit"
              color="blue"
              size="large"
              fullWidth
              disabled={submitting || isSubmitDisabled || isFieldsDisabled}
              data-testid="submit-button"
            >
              {isNew ? 'Create' : 'Update'}
            </Button>
          </form>
        )
      }}
    >
      MembershipForm
    </FinalForm>
  )
}
